/**
 * Demo play data for the landing page animation.
 * Exported from a real PlaySmith play to ensure authentic rendering.
 */

import type { Drawing, PathStyle } from '@/types'
import type { PlayContent } from '@/api/queries/PlayQueries'

// Style used by the real play's drawings
const routeStyle: PathStyle = {
	color: '#000000',
	strokeWidth: 2,
	lineStyle: 'solid',
	lineEnd: 'arrow',
	pathMode: 'sharp',
}

const curveRouteStyle: PathStyle = {
	...routeStyle,
	pathMode: 'curve',
}

/**
 * Demo play — exported from a real PlaySmith play.
 *
 * Formation: Spread with flankers and a singleback set.
 * Routes: X drag, Z comeback, Y crossing, F out-and-up, H swing right.
 */
export const DEMO_PLAY: PlayContent = {
	id: 'demo-play',
	name: 'Demo Play',
	teamId: null,
	players: [
		// Offensive line
		{ id: 'lt', x: 68, y: 28, label: 'LT', color: '#FFFFFF' },
		{ id: 'lg', x: 74, y: 28, label: 'LG', color: '#FFFFFF' },
		{ id: 'c', x: 80, y: 28, label: 'C', color: '#FFFFFF' },
		{ id: 'rg', x: 86, y: 28, label: 'RG', color: '#FFFFFF' },
		{ id: 'rt', x: 92, y: 28, label: 'RT', color: '#FFFFFF' },
		// Skill positions
		{ id: 'x', x: 15, y: 28, label: 'X', color: '#FFFFFF' },
		{ id: 'z', x: 141, y: 28, label: 'Z', color: '#FFFFFF' },
		{ id: 'qb', x: 80, y: 13, label: 'Q', color: '#FFFFFF' },
		{ id: 'y', x: 117, y: 24, label: 'Y', color: '#FFFFFF' },
		{ id: 'f', x: 40, y: 24, label: 'F', color: '#FFFFFF' },
		{ id: 'h', x: 92, y: 13, label: 'H', color: '#FFFFFF' },
	],
	drawings: [
		// X: drag route — stems upfield, breaks right across the field
		{
			id: 'draw-x',
			playerId: 'x',
			linkedPointId: 'x-p1',
			points: {
				'x-p1': { id: 'x-p1', x: 15, y: 28, type: 'start' },
				'x-p2': { id: 'x-p2', x: 14.93, y: 59.46, type: 'corner' },
				'x-p3': { id: 'x-p3', x: 22.51, y: 68.60, type: 'corner' },
				'x-p4': { id: 'x-p4', x: 43.09, y: 68.88, type: 'corner' },
				'x-p5': { id: 'x-p5', x: 65.90, y: 68.58, type: 'end' },
			},
			segments: [
				{ type: 'line', pointIds: ['x-p1', 'x-p2'] },
				{ type: 'line', pointIds: ['x-p2', 'x-p3'] },
				{ type: 'line', pointIds: ['x-p3', 'x-p4'] },
				{ type: 'line', pointIds: ['x-p4', 'x-p5'] },
			],
			style: routeStyle,
			annotations: [],
		},
		// Z: streak to comeback — stems upfield, breaks back inside
		{
			id: 'draw-z',
			playerId: 'z',
			linkedPointId: 'z-p1',
			points: {
				'z-p1': { id: 'z-p1', x: 141, y: 28, type: 'start' },
				'z-p2': { id: 'z-p2', x: 141.17, y: 60.01, type: 'corner' },
				'z-p3': { id: 'z-p3', x: 125.86, y: 83.19, type: 'end' },
			},
			segments: [
				{ type: 'line', pointIds: ['z-p1', 'z-p2'] },
				{ type: 'line', pointIds: ['z-p2', 'z-p3'] },
			],
			style: routeStyle,
			annotations: [],
		},
		// Y: crossing route — breaks inside and runs across the field
		{
			id: 'draw-y',
			playerId: 'y',
			linkedPointId: 'y-p1',
			points: {
				'y-p1': { id: 'y-p1', x: 117, y: 24, type: 'start' },
				'y-p2': { id: 'y-p2', x: 111.34, y: 34.41, type: 'corner' },
				'y-p3': { id: 'y-p3', x: 97.37, y: 42.47, type: 'corner' },
				'y-p4': { id: 'y-p4', x: 46.52, y: 42.47, type: 'end' },
			},
			segments: [
				{ type: 'line', pointIds: ['y-p1', 'y-p2'] },
				{ type: 'line', pointIds: ['y-p2', 'y-p3'] },
				{ type: 'line', pointIds: ['y-p3', 'y-p4'] },
			],
			style: curveRouteStyle,
			annotations: [],
		},
		// F: out-and-up — stems upfield, breaks out, then runs upfield again
		{
			id: 'draw-f',
			playerId: 'f',
			linkedPointId: 'f-p1',
			points: {
				'f-p1': { id: 'f-p1', x: 40, y: 24, type: 'start' },
				'f-p2': { id: 'f-p2', x: 40.27, y: 45.93, type: 'corner' },
				'f-p3': { id: 'f-p3', x: 9.10, y: 45.95, type: 'corner' },
				'f-p4': { id: 'f-p4', x: 9.16, y: 69.72, type: 'corner' },
				'f-p5': { id: 'f-p5', x: 9.17, y: 84.38, type: 'end' },
			},
			segments: [
				{ type: 'line', pointIds: ['f-p1', 'f-p2'] },
				{ type: 'line', pointIds: ['f-p2', 'f-p3'] },
				{ type: 'line', pointIds: ['f-p3', 'f-p4'] },
				{ type: 'line', pointIds: ['f-p4', 'f-p5'] },
			],
			style: curveRouteStyle,
			annotations: [],
		},
		// H: swing right — arcs out to the right behind the line
		{
			id: 'draw-h',
			playerId: 'h',
			linkedPointId: 'h-p1',
			points: {
				'h-p1': { id: 'h-p1', x: 92, y: 13, type: 'start' },
				'h-p2': { id: 'h-p2', x: 111.60, y: 7.84, type: 'corner' },
				'h-p3': { id: 'h-p3', x: 132.66, y: 7.61, type: 'corner' },
				'h-p4': { id: 'h-p4', x: 143.97, y: 13.79, type: 'end' },
			},
			segments: [
				{ type: 'line', pointIds: ['h-p1', 'h-p2'] },
				{ type: 'line', pointIds: ['h-p2', 'h-p3'] },
				{ type: 'line', pointIds: ['h-p3', 'h-p4'] },
			],
			style: curveRouteStyle,
			annotations: [],
		},
	],
}
